import { useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { formatTarget } from '@/features/ogsm/target-format'
import { useOgsmApi } from '@/hooks/use-ogsm'
import { useOgsmActualsQuery } from '@/hooks/use-ogsm-data'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { OgsmBoardNode } from '@/types/api'

export interface ActualHistoryProps {
  companyId: string
  node: OgsmBoardNode
  /** `stack`: popover (value left, date right). `columns`: two subgrid cells under the dialog's value and date inputs. */
  layout: 'stack' | 'columns'
  /** Entries shown before "Show all N actuals"; omitted = all. */
  limit?: number
  className?: string
}

/**
 * A goal's past actuals as the backend serves them (newest first). It fetches
 * on mount, so callers mount it only while it is visible.
 */
export function ActualHistory({
  companyId,
  node,
  layout,
  limit,
  className,
}: ActualHistoryProps) {
  const history = useOgsmActualsQuery(useOgsmApi(companyId), node.id, true)
  const [showAll, setShowAll] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const columns = layout === 'columns'
  const cell = columns ? 'px-3' : undefined
  const data = history.data ?? []
  const capped = limit != null && !showAll
  const shown = capped ? data.slice(0, limit) : data

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className={cn(
        'outline-none',
        columns
          ? 'col-span-2 grid grid-cols-subgrid gap-y-1'
          : 'flex flex-col gap-1',
        className
      )}
    >
      <Typography
        variant="overline"
        className={cn('text-primary/60', columns && 'col-span-2 px-3')}
      >
        {OGSM_COPY.actualHistory}
      </Typography>
      {history.isLoading && (
        <Typography
          variant="body-sm"
          className={cn('text-primary/60', columns && 'col-span-2 px-3')}
        >
          {OGSM_COPY.loadingHistory}
        </Typography>
      )}
      {history.data?.length === 0 && (
        <Typography
          variant="body-sm"
          className={cn('text-primary/60', columns && 'col-span-2 px-3')}
        >
          {OGSM_COPY.noActuals}
        </Typography>
      )}
      {shown.length > 0 && (
        <ol
          className={
            columns
              ? 'col-span-2 grid grid-cols-subgrid gap-y-1'
              : 'flex flex-col gap-1'
          }
        >
          {shown.map((actual, index) => (
            <li
              key={actual.asOfDate}
              className={cn(
                'tabular-nums',
                columns
                  ? 'col-span-2 grid grid-cols-subgrid'
                  : 'flex justify-between gap-2',
                index === 0 ? 'text-primary' : 'text-primary/60'
              )}
            >
              <Typography as="span" variant="body-sm" className={cell}>
                {formatTarget(
                  node,
                  { value: actual.value, text: null, date: null },
                  false
                )}
              </Typography>
              <Typography as="span" variant="body-sm" className={cell}>
                {formatDate(actual.asOfDate)}
              </Typography>
            </li>
          ))}
        </ol>
      )}
      {capped && data.length > limit && (
        <Button
          type="button"
          variant="link"
          className={cn(
            'h-auto justify-self-start p-0 text-xs',
            columns && 'col-span-2 px-3'
          )}
          onClick={() => {
            // The button unmounts; keep focus in the dialog, not on <body>.
            rootRef.current?.focus()
            setShowAll(true)
          }}
        >
          {OGSM_COPY.historyShowAll(data.length)}
        </Button>
      )}
      {history.isError && (
        <div
          className={cn(
            'flex flex-wrap items-baseline gap-x-1',
            columns && 'col-span-2 px-3'
          )}
        >
          <Typography
            as="span"
            variant="body-sm"
            className="text-destructive"
            role="alert"
          >
            {OGSM_COPY.historyFailed}
          </Typography>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs"
            onClick={() => {
              rootRef.current?.focus()
              void history.refetch()
            }}
          >
            {OGSM_COPY.tryAgain}
          </Button>
        </div>
      )}
    </div>
  )
}
